import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/Auth';
import Logo from './Logo';

/**
 * Joining with an invite.
 *
 * Two steps on one card: the code first, and only once the server has said it
 * is good, a username and password. Nothing about the account form is shown to
 * someone without a working code, so this page is no more of a door than the
 * login is. An invite link (/signup?code=...) arrives with the code filled in
 * and checks it straight away.
 */
const Signup = () => {
  const { user, ready, signUp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [code, setCode] = useState(params.get('code') || '');
  const [checked, setChecked] = useState(false);
  const [minPassword, setMinPassword] = useState(10);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const check = async (value) => {
    if (busy || !value.trim()) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post('/api/invite/check', { code: value });
      setMinPassword(data.data?.minPassword || 10);
      setChecked(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not check the code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // A link with the code in it checks itself; nobody should have to press a
  // button to confirm what they were sent
  useEffect(() => {
    const fromLink = params.get('code');
    if (fromLink) check(fromLink);
  }, []);

  const create = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (password !== again) {
      setError('Those passwords do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await signUp(code, username, password);
      navigate('/home', { replace: true });
    } catch (err) {
      const message = err.response?.data?.error || 'Could not create the account. Try again.';
      setError(message);
      // A code that went bad in the meantime (revoked, expired, just used)
      // sends them back to the first step rather than leaving a dead form
      if (/invite code/i.test(message)) setChecked(false);
      setBusy(false);
    }
  };

  if (!ready) return null;
  // Already signed in: an invite is for someone else
  if (user) return <Navigate to="/home" replace />;

  return (
    <div className="login">
      {!checked ? (
        <form
          className="login-card"
          onSubmit={(event) => {
            event.preventDefault();
            check(code);
          }}
        >
          <div className="login-brand">
            <Logo />
          </div>

          <h1 className="login-title">Have an invite?</h1>
          <p className="login-lede">Enter the code you were sent.</p>

          <label className="login-field">
            <span className="login-k">Invite code</span>
            <input
              type="text"
              className="login-code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck="false"
              autoFocus
              required
            />
          </label>

          {error && <div className="alert login-alert">{error}</div>}

          <button type="submit" className="btn btn--play login-submit" disabled={busy}>
            {busy ? <span className="play-spin" /> : null}
            {busy ? 'Checking…' : 'Continue'}
          </button>

          <Link to="/login" className="login-aside">
            Already have an account? Sign in
          </Link>
        </form>
      ) : (
        <form className="login-card" onSubmit={create}>
          <div className="login-brand">
            <Logo />
          </div>

          <h1 className="login-title">You're in</h1>
          <p className="login-lede">Pick the name and password you'll sign in with.</p>

          <label className="login-field">
            <span className="login-k">Username</span>
            <input
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value.toLowerCase())}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              minLength={2}
              maxLength={24}
              pattern="[a-z0-9][a-z0-9._\-]{1,23}"
              title="2-24 characters: letters, numbers, dots, dashes or underscores"
              autoFocus
              required
            />
          </label>

          <label className="login-field">
            <span className="login-k">Password · at least {minPassword} characters</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              minLength={minPassword}
              maxLength={256}
              required
            />
          </label>

          <label className="login-field">
            <span className="login-k">Repeat password</span>
            <input
              type="password"
              value={again}
              onChange={(event) => setAgain(event.target.value)}
              autoComplete="new-password"
              minLength={minPassword}
              maxLength={256}
              required
            />
          </label>

          {error && <div className="alert login-alert">{error}</div>}

          <button type="submit" className="btn btn--play login-submit" disabled={busy}>
            {busy ? <span className="play-spin" /> : null}
            {busy ? 'Creating…' : 'Create account'}
          </button>
        </form>
      )}
    </div>
  );
};

export default Signup;
